import React from 'react';
import { createGlobalStyle } from 'styled-components';
import {
  accentBarStyle,
  animMs,
  barStyle,
  bodyStyle,
  clockBoxStyle,
  clockStyle,
  contentIndentFor,
  digitBoxStyle,
  dividerStyle,
  formatSeconds,
  formatSpan,
  formatSubathonClock,
  labelStyle,
  mutedStyle,
  surfaceStyle,
  withAlpha,
} from './styleUtils';
import { CreditFlash } from './useSubathonOverlay';
import {
  SubathonElements,
  SubathonEventType,
  SubathonState,
  SubathonStyle,
  SubathonTimer,
} from './types';

export const SubathonOverlayGlobalStyle = createGlobalStyle`
  html, body, #root {
    margin: 0;
    padding: 0;
    width: 100%;
    height: 100%;
    background: transparent !important;
    overflow: hidden;
  }

  @keyframes subathon-flash-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes subathon-pulse {
    0% { transform: scale(1); }
    35% { transform: scale(1.035); }
    100% { transform: scale(1); }
  }

  @keyframes subathon-blink {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.45; }
  }
`;

const TYPE_LABELS: Record<SubathonEventType, string> = {
  follow: 'Follows',
  raid: 'Raids',
  bits: 'Bits',
  sub: 'Subs',
  resub: 'Resubs',
  subgift: 'Subs regaladas',
  cafecito: 'Cafecitos',
  coffee: 'Ko-fi',
  tip: 'Propinas',
  chest: 'Cofres',
  manual: 'Ajustes',
};

/* ------------------------------------------------------------------ */

/** El reloj, con la opción de un recuadro por dígito. */
const Clock: React.FC<{ style: SubathonStyle; text: string; urgent: boolean }> = ({
  style,
  text,
  urgent,
}) => {
  const base = clockStyle(style, urgent);

  if (!style.digitBoxes) {
    return <div style={{ ...base, ...clockBoxStyle(style) }}>{text}</div>;
  }

  const box = digitBoxStyle(style);
  return (
    <div style={{ ...base, display: 'flex', gap: Math.round(style.timerFontSize * 0.08) }}>
      {text.split('').map((char, index) =>
        char === ':' ? (
          <span key={index} style={{ opacity: 0.55, alignSelf: 'center' }}>
            :
          </span>
        ) : (
          <span key={index} style={box}>
            {char}
          </span>
        ),
      )}
    </div>
  );
};

const StatePill: React.FC<{ style: SubathonStyle; text: string; blink?: boolean }> = ({
  style,
  text,
  blink,
}) => (
  <span
    style={{
      ...mutedStyle(style),
      alignSelf: 'inherit',
      padding: '2px 10px',
      borderRadius: 999,
      border: `1px solid ${withAlpha(style.accentColor, 0.38)}`,
      background: withAlpha(style.accentColor, 0.12),
      color: style.accentColor,
      textTransform: style.uppercaseLabels ? 'uppercase' : 'none',
      letterSpacing: 1,
      animation:
        blink && style.animationsEnabled
          ? `subathon-blink ${animMs(style, 1800)}ms ease-in-out infinite`
          : undefined,
    }}
  >
    {text}
  </span>
);

/** Los avisos de aportes: «+5 min · sub de Juan». Visuales, nunca sonoros. */
const Feed: React.FC<{
  style: SubathonStyle;
  elements: SubathonElements;
  flashes: CreditFlash[];
}> = ({ style, elements, flashes }) => {
  const seconds = Math.max(2, Number(elements.feedSeconds) || 12);
  const limit = Math.max(1, Number(elements.feedItems) || 3);

  const visible = flashes
    .filter((flash) => Date.now() - flash.at < seconds * 1000)
    .slice(-limit)
    .reverse();

  if (!visible.length) return null;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: Math.max(3, Math.round(style.gap * 0.35)),
        alignSelf: 'stretch',
        alignItems: 'inherit',
      }}
    >
      {visible.map((flash) => (
        <div
          key={flash.id}
          style={{
            ...bodyStyle(style, { fontSize: Math.round(style.bodyFontSize * 0.92) }),
            display: 'flex',
            gap: 8,
            alignItems: 'baseline',
            animation: style.animationsEnabled
              ? `subathon-flash-in ${animMs(style, 320)}ms ease-out`
              : undefined,
          }}
        >
          <strong style={{ color: style.accentColor, whiteSpace: 'nowrap' }}>
            {flash.secondsAdded >= 0 ? '+' : ''}
            {formatSeconds(flash.secondsAdded)}
          </strong>
          <span style={{ color: style.mutedColor }}>{flash.label}</span>
        </div>
      ))}
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface TimerProps {
  style: SubathonStyle;
  elements: SubathonElements;
  timer: SubathonTimer;
  remainingMs: number;
  totalAddedMs: number;
  flashes: CreditFlash[];
}

export const SubathonTimerBlock: React.FC<TimerProps> = ({
  style,
  elements,
  timer,
  remainingMs,
  totalAddedMs,
  flashes,
}) => {
  const urgentThreshold = Math.max(0, Number(style.urgentUnderMinutes) || 0) * 60 * 1000;
  const urgent = timer.status === 'running' && urgentThreshold > 0 && remainingMs <= urgentThreshold;
  const finished = timer.status === 'finished';

  const clock = formatSubathonClock(
    remainingMs,
    elements.showHours !== false,
    elements.showSeconds !== false,
  );

  const indent = contentIndentFor(style);
  const accent = accentBarStyle(style);
  const inline = style.layout === 'inline';

  const capRatio = timer.capMs > 0 ? remainingMs / timer.capMs : 0;
  const bars = barStyle(style, capRatio);

  const stateText = finished
    ? elements.endText || '¡EXTENSIBLE COMPLETADO!'
    : timer.waiting
      ? 'esperando el primer aporte'
      : timer.status === 'paused'
        ? 'en pausa'
        : '';

  const header = elements.showTitle !== false && (elements.title || 'EXTENSIBLE') ? (
    <div style={labelStyle(style)}>{elements.title || 'EXTENSIBLE'}</div>
  ) : null;

  const meta: React.ReactNode[] = [];
  if (elements.showTotalAdded !== false && totalAddedMs > 0) {
    meta.push(
      <span key="added">
        sumados <strong style={{ color: style.accentColor }}>{formatSpan(totalAddedMs)}</strong>
      </span>,
    );
  }
  if (elements.showCap !== false && timer.capMs > 0) {
    meta.push(
      <span key="cap">
        tope <strong style={{ color: style.secondaryColor }}>{formatSpan(timer.capMs)}</strong>
      </span>,
    );
  }

  const body = (
    <>
      {accent && style.accentBarPosition === 'top' && <div style={accent} />}
      <div
        style={{
          display: 'flex',
          flexDirection: inline ? 'row' : 'column',
          alignItems: inline ? 'center' : 'inherit',
          gap: inline ? Math.max(10, style.gap) : Math.max(2, Math.round(style.gap * 0.5)),
          paddingLeft: indent,
          width: '100%',
        }}
      >
        {inline ? (
          <>
            {header}
            <Clock style={style} text={clock} urgent={urgent} />
          </>
        ) : (
          <>
            {header}
            {style.labelDivider && header && <div style={dividerStyle(style)} />}
            <Clock style={style} text={clock} urgent={urgent} />
          </>
        )}

        {elements.showStatePill !== false && stateText && (
          <StatePill style={style} text={stateText} blink={timer.waiting} />
        )}

        {meta.length > 0 && (
          <div
            style={{
              ...mutedStyle(style),
              display: 'flex',
              gap: 12,
              flexWrap: 'wrap',
              justifyContent: 'inherit',
            }}
          >
            {meta}
          </div>
        )}

        {elements.showCapBar === true && timer.capMs > 0 && (
          <div style={bars.track}>
            <div style={bars.fill} />
          </div>
        )}
      </div>
    </>
  );

  const feed = elements.showFeed !== false ? (
    <Feed style={style} elements={elements} flashes={flashes} />
  ) : null;

  const above = feed && elements.feedPosition === 'above';

  return (
    <div
      style={surfaceStyle(style, {
        position: 'relative',
        animation:
          style.pulseOnCredit && style.animationsEnabled && flashes.length
            ? `subathon-pulse ${animMs(style, 700)}ms ease-out`
            : undefined,
      })}
      // El key hace que el latido se reinicie con cada aporte nuevo.
      key={`pulse-${flashes[flashes.length - 1]?.id || 'none'}`}
    >
      {accent && style.accentBarPosition !== 'top' && <div style={accent} />}
      {above && feed}
      {body}
      {!above && feed}
      {finished && elements.showEndText !== false && !elements.showStatePill && (
        <div style={labelStyle(style, { color: style.urgentColor })}>
          {elements.endText || '¡EXTENSIBLE COMPLETADO!'}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */

interface SummaryProps {
  style: SubathonStyle;
  elements: SubathonElements;
  state: SubathonState;
  remainingMs: number;
}

export const SubathonSummaryBlock: React.FC<SummaryProps> = ({ style, elements, state, remainingMs }) => {
  const { timer, totals } = state;
  const indent = contentIndentFor(style);
  const accent = accentBarStyle(style);
  const compact = elements.compact === true;

  const duration = timer.initialMs + Math.max(0, timer.addedMs);
  const elapsed = Math.max(0, duration - remainingMs);

  const breakdown = Object.entries(totals.byType || {})
    .filter(([, info]) => (info?.seconds || 0) > 0)
    .sort((a, b) => (b[1]?.seconds || 0) - (a[1]?.seconds || 0));

  const rowGap = Math.max(3, Math.round(style.gap * 0.4));
  // Si se apagaron los tres números, la fila entera desaparece.
  const statsRow =
    elements.showElapsed !== false || elements.showAdded !== false || elements.showEventsCount !== false;

  return (
    <div style={surfaceStyle(style, { position: 'relative' })}>
      {accent && style.accentBarPosition !== 'top' && <div style={accent} />}
      {accent && style.accentBarPosition === 'top' && <div style={accent} />}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: Math.max(4, Math.round(style.gap * 0.6)),
          paddingLeft: indent,
          width: '100%',
          alignItems: 'inherit',
        }}
      >
        {elements.showTitle !== false && (
          <>
            <div style={labelStyle(style)}>{elements.title || 'RESUMEN DEL EXTENSIBLE'}</div>
            {style.labelDivider && <div style={dividerStyle(style)} />}
          </>
        )}

        {elements.showDuration !== false && statsRow && (
          <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'inherit' }}>
            {elements.showElapsed !== false && (
              <div>
                <div style={mutedStyle(style)}>En vivo</div>
                <div style={bodyStyle(style, { fontSize: Math.round(style.bodyFontSize * 1.35), fontWeight: 700 })}>
                  {formatSpan(elapsed)}
                </div>
              </div>
            )}
            {elements.showAdded !== false && (
              <div>
                <div style={mutedStyle(style)}>Extendido</div>
                <div
                  style={bodyStyle(style, {
                    fontSize: Math.round(style.bodyFontSize * 1.35),
                    fontWeight: 700,
                    color: style.accentColor,
                  })}
                >
                  {formatSpan(timer.addedMs)}
                </div>
              </div>
            )}
            {elements.showEventsCount !== false && (
              <div>
                <div style={mutedStyle(style)}>Aportes</div>
                <div style={bodyStyle(style, { fontSize: Math.round(style.bodyFontSize * 1.35), fontWeight: 700 })}>
                  {totals.events}
                </div>
              </div>
            )}
          </div>
        )}

        {elements.showBreakdown !== false && breakdown.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: rowGap, alignSelf: 'stretch' }}>
            {breakdown.slice(0, compact ? 4 : 8).map(([type, info]) => {
              const ratio = totals.seconds > 0 ? (info?.seconds || 0) / totals.seconds : 0;
              const bars = barStyle(style, ratio);
              return (
                <div key={type} style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <div style={{ ...bodyStyle(style), display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                    <span>{TYPE_LABELS[type as SubathonEventType] || type}</span>
                    <span style={{ color: style.mutedColor, whiteSpace: 'nowrap' }}>
                      {formatSpan((info?.seconds || 0) * 1000)}
                    </span>
                  </div>
                  {!compact && (
                    <div style={bars.track}>
                      <div style={bars.fill} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {elements.showTopContributors !== false && totals.top.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: rowGap, alignSelf: 'stretch' }}>
            <div style={mutedStyle(style, { textTransform: style.uppercaseLabels ? 'uppercase' : 'none' })}>
              Quiénes lo extendieron
            </div>
            {totals.top.slice(0, Math.max(1, Number(elements.topCount) || 5)).map((row, index) => (
              <div
                key={row.displayName}
                style={{ ...bodyStyle(style), display: 'flex', justifyContent: 'space-between', gap: 10 }}
              >
                <span>
                  <span style={{ color: style.secondaryColor, marginRight: 6 }}>{index + 1}.</span>
                  {row.displayName}
                </span>
                <span style={{ color: style.accentColor, whiteSpace: 'nowrap' }}>
                  {formatSpan(row.seconds * 1000)}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
